import { useCallback } from 'react';
import { useMutation } from '@apollo/client';

import { CREATE_PERSON_DOCUMENT_UPLOAD } from '../mutations/PERSON_REGISTRATION';

export interface PendingDocument {
    kind: string;
    file: File;
}

/**
 * A document that did not upload, **and why**.
 *
 * The reason is the whole point. This hook originally returned bare filenames, so every
 * possible cause — a permission refusal, an unsupported file type, a dead presign, a 403 from
 * S3 — surfaced to the user as the identical, useless "These did not upload: x.jpg", and left
 * nothing in the server log either. Diagnosing one meant reproducing it by hand.
 */
export interface FailedDocument {
    fileName: string;
    reason: string;
}

/**
 * Upload supporting documents against a person.
 *
 * Two-step, like every other upload in this app: the server creates the row and hands back a
 * presigned PUT, the browser sends the bytes straight to S3.
 *
 * **The `Content-Type` header must match what was passed to the mutation.** The presign for
 * these signs content type (unlike the photo path, which signs `application/octet-stream` for
 * back-compatibility with old iOS builds), so a mismatched header is a SigV4 failure — and a
 * PDF that did sneak through as `application/octet-stream` would store fine and then refuse to
 * open in a browser.
 */
export const usePersonDocumentUpload = () => {
    const [createUpload] = useMutation(CREATE_PERSON_DOCUMENT_UPLOAD);

    return useCallback(async (personId: number, documents: PendingDocument[]) => {
        const failed: FailedDocument[] = [];

        for (const { kind, file } of documents) {
            try {
                // A file the browser cannot type is worth catching here rather than letting the
                // server reject an empty string with a message about "". It happens with files
                // dragged from some apps and with unknown extensions.
                if (!file.type) {
                    failed.push({
                        fileName: file.name,
                        reason: 'The browser could not tell what type of file this is. Try a JPG, PNG or PDF.',
                    });
                    continue;
                }

                const { data } = await createUpload({
                    variables: {
                        personId,
                        kind,
                        fileName: file.name,
                        contentType: file.type,
                    },
                });

                const uploadUrl = data?.createPersonDocumentUpload?.uploadUrl;

                if (!uploadUrl) {
                    failed.push({ fileName: file.name, reason: 'The server did not return an upload link.' });
                    continue;
                }

                const response = await fetch(uploadUrl, {
                    method: 'PUT',
                    body: file,
                    headers: { 'Content-Type': file.type },
                    referrer: 'no-referrer',
                });

                if (!response.ok) {
                    // Distinguish the two halves: the mutation succeeded and S3 refused the
                    // bytes. A 403 here means the presign or the bucket policy, not permission
                    // inside Joyya — which is the opposite end of the system from a GraphQL
                    // error, and worth never confusing again.
                    failed.push({
                        fileName: file.name,
                        reason: `Upload to storage failed (${response.status} ${response.statusText}).`,
                    });
                }
            } catch (e: any) {
                // GraphQL errors arrive here. The message is the server's own — "Unsupported
                // file type: …", "That person is not a member of this organisation", and so on.
                console.error('Document upload failed', file.name, e);
                failed.push({
                    fileName: file.name,
                    reason: e?.message || 'Something went wrong.',
                });
            }
        }

        // Reported rather than thrown. By the time this runs the person has already been
        // created, and losing that save because an attachment failed would be the worse
        // outcome — the caller surfaces the reasons so they can be acted on.
        return failed;
    }, [createUpload]);
};
